import type { ComponentProps } from 'preact';
import { formatTime } from '../formatters';
import type { ItineraryBoardingStep, ItineraryStep } from '../itinerarySteps';
import { OPERATOR_HEADSIGN_MODES } from '../../../../transportModes';
import { JourneyPlanBoardingStepDetails } from './BoardingStepDetails';
import {
  JourneyPlanItineraryStep,
  type JourneyPlanItineraryStepProps,
} from './ItineraryStep';
import styles from './JourneyPlanItineraryStep.module.css';

interface JourneyPlanTypedItineraryStepProps extends ComponentProps<'li'> {
  step: ItineraryStep;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  onShowStopDeparturesDetails?: (step: ItineraryBoardingStep) => void;
  onShowLineTimetableDetails?: (step: ItineraryBoardingStep) => void;
}

interface PositionedStepProps {
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

type StepRenderProps = PositionedStepProps & ComponentProps<'li'>;

function assertNever(value: never): never {
  throw new Error(`Unsupported itinerary step: ${JSON.stringify(value)}`);
}

function formatCount(value: number, singular: string, plural: string): string {
  return `${value} ${value === 1 ? singular : plural}`;
}

function getWalkStepDetails(
  step: Extract<ItineraryStep, { kind: 'walk' }>,
): string {
  const details = [formatCount(step.walkMinutes, 'min', 'mins')];

  if (step.paces !== undefined) {
    details.push(formatCount(step.paces, 'step', 'steps'));
  }

  return details.join(', ');
}

function renderEmphasisedAction(step: ItineraryStep) {
  switch (step.kind) {
    case 'start':
    case 'end':
      return step.action;
    case 'walk':
      if (step.isToDestination) {
        return step.action;
      }

      if (step.toMode && step.toStop) {
        return (
          <>
            Walk to the{' '}
            <span className={styles.actionEmphasis}>{step.toMode} stop</span>
            <br />
            <span className={styles.actionSecondaryLine}>
              <span className={styles.actionEmphasis}>{step.toStop}</span>
            </span>
          </>
        );
      }

      if (step.toStop) {
        return (
          <>
            Walk to <span className={styles.actionEmphasis}>{step.toStop}</span>
          </>
        );
      }

      return step.action;
    case 'wait':
      if (!step.atStop) {
        return step.action;
      }

      return (
        <>
          Wait {step.waitMinutes} min at{' '}
          <span className={styles.actionEmphasis}>{step.atStop}</span>
        </>
      );
    case 'boarding': {
      const isOperatorHeadsignMode =
        step.headsign && OPERATOR_HEADSIGN_MODES.has(step.mode);

      if (isOperatorHeadsignMode) {
        const label = step.operator?.name ?? step.service;
        return (
          <>
            Board <span className={styles.actionEmphasis}>{label}</span>
            <br />
            <span className={styles.actionSecondaryLine}>
              towards{' '}
              <span className={styles.actionEmphasis}>{step.headsign}</span>
            </span>
          </>
        );
      }

      return (
        <>
          Board <span className={styles.actionEmphasis}>{step.service}</span>
        </>
      );
    }
    case 'alighting':
      if (!step.stopName) {
        return step.action;
      }

      return (
        <>
          Get off at{' '}
          <span className={styles.actionEmphasis}>{step.stopName}</span>
        </>
      );
  }

  return assertNever(step);
}

function toItineraryStepProps(
  step: ItineraryStep,
  options: {
    onShowStopDeparturesDetails?: (step: ItineraryBoardingStep) => void;
    onShowLineTimetableDetails?: (step: ItineraryBoardingStep) => void;
  },
): Pick<
  JourneyPlanItineraryStepProps,
  | 'children'
  | 'details'
  | 'hasContentDivider'
  | 'timeDateTime'
  | 'marker'
  | 'disruption'
> {
  switch (step.kind) {
    case 'start':
    case 'end':
      return {
        children: renderEmphasisedAction(step),
        details: undefined,
        hasContentDivider: true,
        timeDateTime: 'timeDateTime' in step ? step.timeDateTime : undefined,
        marker: {
          variant: 'default',
          circleColor: 'var(--internal-text-base1-primary)',
          circleBorderWidth: '2px',
        },
        disruption: step.disruption,
      };
    case 'walk':
      return {
        children: renderEmphasisedAction(step),
        details: getWalkStepDetails(step),
        hasContentDivider: true,
        timeDateTime: undefined,
        marker: { variant: 'default', hideCircle: true },
        disruption: step.disruption,
      };
    case 'wait':
      return {
        children: renderEmphasisedAction(step),
        details: undefined,
        hasContentDivider: true,
        timeDateTime: undefined,
        marker: { variant: 'default', hideCircle: true },
        disruption: step.disruption,
      };
    case 'boarding':
      return {
        children: renderEmphasisedAction(step),
        details: (
          <JourneyPlanBoardingStepDetails
            step={step}
            onShowStopDeparturesDetails={options.onShowStopDeparturesDetails}
            onShowLineTimetableDetails={options.onShowLineTimetableDetails}
          />
        ),
        hasContentDivider: false, // No divider as it flows into the next alighting step which will have the divider
        timeDateTime: step.timeDateTime,
        marker: {
          variant: 'line',
          lineColor: step.lineColor,
          lineTopColor: 'var(--internal-divider)',
          lineBottomColor: step.lineColor,
        },
        disruption: step.disruption,
      };
    case 'alighting':
      return {
        children: renderEmphasisedAction(step),
        details: undefined,
        hasContentDivider: true,
        timeDateTime: step.timeDateTime,
        marker: {
          variant: 'default',
          lineColor: step.lineColor,
          lineBottomColor: 'var(--internal-divider)',
        },
        disruption: step.disruption,
      };
  }

  return assertNever(step);
}

export function JourneyPlanTypedItineraryStep({
  step,
  isFirstStep,
  isLastStep,
  onShowStopDeparturesDetails,
  onShowLineTimetableDetails,
  ...props
}: JourneyPlanTypedItineraryStepProps) {
  const positionProps: StepRenderProps = {
    isFirstStep,
    isLastStep,
    ...props,
  };
  const itineraryStepProps = toItineraryStepProps(step, {
    onShowStopDeparturesDetails,
    onShowLineTimetableDetails,
  });
  const timeLabel = itineraryStepProps.timeDateTime
    ? formatTime(itineraryStepProps.timeDateTime)
    : undefined;

  return (
    <JourneyPlanItineraryStep
      {...positionProps}
      {...itineraryStepProps}
      timeLabel={timeLabel}
    />
  );
}
